import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { type GetManyForUserResponseDto, JobsService, LayoutsService } from 'app/API';
import { useEffect, useState } from 'react';

import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

export const ChooseCloudLayoutModal = ({ id, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { workspace, organization } = useAuthContext();
  const [selectedCloudLayout, setSelectedCloudLayout] = useState('null');
  const [isLoading, setIsLoading] = useState(false);
  const [layouts, setLayouts] = useState<GetManyForUserResponseDto[] | undefined>([]);

  const submit = async () => {
    setIsLoading(true);
    await call(
      JobsService.updateLayout({
        jobId: id,
        wsid: workspace?.id ?? '',
        requestBody: { layoutId: selectedCloudLayout === 'null' ? null : selectedCloudLayout },
      }),
    );
    setIsLoading(false);
    onSuccess();
  };

  const layoutsList = [
    {
      label: 'None',
      value: 'null',
    },
    ...(layouts || []).map((l) => ({
      value: l.id,
      label: l.name,
    })),
  ];

  useEffect(() => {
    (async () => {
      if (!workspace?.id || !organization?.id) {
        setIsLoading(true);
        return;
      }
      const jobData = await call(JobsService.getJobById({ jobId: id, wsid: workspace.id }));
      setSelectedCloudLayout(jobData?.layoutId || 'null');

      const layouts = await call(LayoutsService.getOrganizationLayouts({ oid: organization.id }));
      setLayouts(layouts);

      setIsLoading(false);
    })();
  }, []);

  return (
    <ModalWrapper width="487px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Insert layout</ModalTitle>

      <Content>
        <div className="description">
          You can choose a layout for this Job. It will be automatically applied to the transcript.
        </div>

        <Select
          name="layoutId"
          className="layout-select"
          label="Cloud Layout"
          value={selectedCloudLayout}
          disabled={layoutsList.length < 2 || isLoading}
          onChange={(_, value) => setSelectedCloudLayout(value)}
          options={layoutsList}
          required={false}
        />

        <div className="footer-description">
          * If you don’t have a cloud layout available, please visit this page for instructions to create cloud layouts
          for use in job processing.
        </div>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={!selectedCloudLayout.length || isLoading} onClick={submit}>
            Apply
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
